// src/app/return/return.component.ts
import { Component, inject, signal, OnInit, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { environment } from '../../../../../environments/environment';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { SuccessError } from "../success-error/success-error";
import confetti from 'canvas-confetti';
import { HapticsService } from '../../services/haptics.service';
import { ListingFormDialog } from '../../../../features/sales/components/common-forms/sales-consultant-listing-form/listing-form-dialog/listing-form-dialog';
import { DialogService } from '../../../services/dialog.service';
import { SeoService } from '../../../services/seo.service';
import { AuthService } from '../../../../core/services/auth.service';

interface SessionStatus {
  status: 'complete' | 'expired' | 'open' | string;
  payment_status: 'paid' | 'unpaid' | 'no_payment_required' | string;
  customer_email?: string;
  amount_total?: number;
  currency?: string;
  error?: string;
}

@Component({
  selector: 'app-return',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    SuccessError,
    ListingFormDialog
],
  templateUrl: './return.html',
  styles: [`
    //.return-container { font-family: system-ui, sans-serif; }
    //button { padding: 0.8rem 1.5rem; background: #635bff; color: white; border: none; border-radius: 6px; cursor: pointer; }
    //button:hover { background: #4f3ed6; }
  `]
})
export class ReturnComponent implements OnInit {
  private haptics = inject(HapticsService);
  private router = inject(Router);
  private dialog = inject(DialogService);
  private platformId = inject(PLATFORM_ID);

  private route = inject(ActivatedRoute);
  private http = inject(HttpClient);
  private seo = inject(SeoService);
  private auth = inject(AuthService);

  apiUrl = environment.apiUrl;

  loading = signal(true);
  error = signal<string | null>(null);
  session = signal<SessionStatus | null>(null);

  ngOnInit() {
    this.seo.updateMetaTags("Payment Result")

    //console.log('ReturnComponent loaded – URL:', window.location.href);

    const sessionId = this.route.snapshot.queryParamMap.get('session_id');
    //console.log('Session ID from query:', sessionId);

    this.loading.set(true);

    if (!sessionId) {
      this.error.set('No session ID – check Stripe redirect');
      this.loading.set(false);
      // Do NOT navigate here yet
      // setTimeout(() => this.router.navigate(['/']), 5000); // test with delay
      return;
    }

    // const sessionId = this.route.snapshot.queryParamMap.get('session_id');

    if (!sessionId) {
      this.error.set('No session ID found in the URL. If you just paid, check your email for confirmation.');
      this.loading.set(false);
      return;
    }

    // Call your backend to retrieve & check session status
    this.http.get<SessionStatus>(`${this.apiUrl}/api/checkout/session-status?session_id=${sessionId}`)
      .subscribe({
        next: (data) => {
          console.log('Session status:', data);
          this.session.set(data);
          this.confetti();
          this.clearDraft();
          this.loading.set(false);
        },
        error: (err) => {
          console.error('Failed to fetch session status', err);
          this.error.set('Unable to load payment details right now. Your order may still have processed — check your email.');
          this.loading.set(false);
        }
      });
  }

  private getDraftKey() {
    return `property-draft-${this.auth.userSubject.value?.id}`;
  }

  private clearDraft() {
    const key = this.getDraftKey();
    localStorage.removeItem(key);
    console.log('✅ Payment successful — cleared draft');
  }
  
  formatAmount(amount: number, currency: string): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'usd'
    }).format(amount / 100);
  }

  retry() {
    // Redirect back to checkout or home — customize as needed
    window.location.href = '/checkout';
  }

  goToProperties() {
    this.router.navigate(['/profile/manage-properties'])
  }

  goToSalesListingForm() {
    this.haptics.click();
    this.dialog.open(ListingFormDialog, {
      width: '100vw',
      height: '100vh',
      maxWidth: '100vw',
      maxHeight: '100vh',
      autoFocus: false,
      panelClass: 'sales-listing-dialog'
    });
  }

  public confetti() {
    if (isPlatformBrowser(this.platformId)) {
      const duration = 15 * 300;
      const animationEnd = Date.now() + duration;
      const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

      function randomInRange(min: number, max: number) {
        return Math.random() * (max - min) + min;
      }

      var interval = setInterval(function() {
        const timeLeft = animationEnd - Date.now();

        if (timeLeft <= 0) {
          return clearInterval(interval);
        }

        const particleCount = 50 * (timeLeft / duration);
        // since particles fall down, start a bit higher than random
        confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 } });
        confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 } });
      }, 250);
    }
  }
}